import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import cn from 'classnames';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Hero } from '@/components/common/Hero';
import Image from 'next/image';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { QUALITY_CONTROL_PAGE_QUERY } from '@/data/qualityControlPageQuery';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { PageHead } from '@/components/common/PageHead';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    title,
    seo,
    qualityControl: { qualityControl },
  } = data.pageBy.translation;

  const qualityMark = qualityControl.qualityMark;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero breadcrumbs={seo?.breadcrumbs}>
            <Heading tag="h1" withDivider content={title || ''} />
            <p
              className="lightHeroText"
              dangerouslySetInnerHTML={{ __html: qualityControl.description }}
            />
          </Hero>
          <Section className="py-15 md:py-25">
            <Heading
              content={qualityControl.subtitle}
              className="accentText text-h3Mobile md:max-w-[85%] md:text-h3 lg:text-h3"
            />
            <ul className="grid gap-y-15 py-15 md:grid-cols-[repeat(2,_280px)] md:grid-rows-2 md:gap-20 md:pb-[132px] md:pt-25 lg:w-[633px] lg:gap-x-8 lg:px-[104px] lg:pb-40">
              {qualityControl.list.map((item, index) => (
                <li
                  key={item.item}
                  className={cn('relative', {
                    'md:col-span-2': index === 0,
                  })}
                >
                  <span className="dark:gray-scale-red-diff text-xl text-accentMain md:text-h3">
                    {'/0' + (index + 1)}
                  </span>
                  <p className="">{item.item}</p>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-8 bg-accentAdditional p-7 text-white md:gap-10 md:p-10 lg:flex-row lg:justify-between lg:p-15">
              <div
                className="!mb-0 lg:flex lg:flex-col lg:items-center lg:justify-center"
                dangerouslySetInnerHTML={{
                  __html: qualityMark.description,
                }}
              />
              <ul className="flex flex-col gap-8 md:flex-row md:justify-around lg:justify-between">
                {qualityMark.marks.map(item => (
                  <li
                    key={item.image.mediaItemUrl}
                    className="lg:flex lg:items-center"
                  >
                    <Image
                      className="mx-auto"
                      src={item.image.mediaItemUrl}
                      alt={item.image.altText}
                      width={item.image.mediaDetails.width}
                      height={item.image.mediaDetails.height}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const { data } = await client.query({
    query: QUALITY_CONTROL_PAGE_QUERY,
    variables: {
      language,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
